import React from 'react';
import { Box, Card, Skeleton, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import { deltaView } from '../../utils/analyticsMetrics';
import { analyticsUiTokens as T } from '../../constants/userAnalytics';

// ── Value slot ───────────────────────────────────────────────────────────────
// The only part of a card that changes when filters change. First load shows a skeleton the
// size of the number; every later refresh keeps the old number and just dims it (the design's
// `.updating` state) — a number is never replaced by a skeleton after the first load.

export const ValueSlot = ({ status, hasData, minHeight, skeletonWidth = 96, testId, children }) => {
  if (!hasData && (status === 'loading' || status === 'idle')) {
    // Same box as the loaded value, so the card does not change height when the number arrives.
    return (
      <Box sx={{ minHeight, display: 'flex', alignItems: 'center' }}>
        <Skeleton variant="rounded" width={skeletonWidth} height={minHeight - 8} data-testid={testId && `${testId}-skeleton`} />
      </Box>
    );
  }
  const refreshing = status === 'refreshing';
  const stale = status === 'error' && hasData;
  return (
    <Box
      data-testid={testId}
      data-refreshing={refreshing ? 'true' : undefined}
      aria-busy={refreshing || undefined}
      title={stale ? "Couldn't refresh — showing the last loaded value" : undefined}
      sx={{
        minHeight,
        display: 'flex',
        alignItems: 'center',
        opacity: refreshing ? 0.3 : stale ? 0.55 : 1,
        transform: refreshing ? 'scale(0.98)' : 'none',
        transformOrigin: 'left center',
        transition: 'opacity 0.18s ease, transform 0.18s ease',
      }}
    >
      {children}
    </Box>
  );
};

const cardSx = (hoverBorder) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  p: { xs: 2, sm: 2.5 },
  bgcolor: '#ffffff',
  borderRadius: '12px',
  border: `1px solid ${T.cardBorder}`,
  boxShadow: '0 1px 2px rgba(15,23,42,0.05)',
  transition: 'all 0.2s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 20px -5px rgba(13,43,87,0.07)',
    borderColor: hoverBorder,
  },
  '&:focus-visible': { outline: `2px solid ${T.navy}`, outlineOffset: 2 },
  '&:hover .ua-label': { color: T.navy },
});

const FOOTER_SX = {
  mt: 2,
  pt: 1.5,
  minHeight: 40,
  boxSizing: 'border-box',
  borderTop: `1px solid ${T.slate[100]}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
};

const viewUsersSx = (hoverColor) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  fontSize: 11,
  fontWeight: 600,
  color: T.navy,
  transition: 'color 0.2s',
  '.MuiCard-root:hover &': { color: hoverColor },
});

const activateOnKey = (onActivate) => (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    onActivate();
  }
};

const DELTA_TONES = {
  emerald: { bg: T.emerald[50], color: T.emerald[700] },
  sky: { bg: T.sky[50], color: T.sky[700] },
  down: { bg: T.red[50], color: T.red[700] },
  neutral: { bg: T.slate[100], color: T.slate[600] },
};

const DeltaBadge = ({ deltaPct, kind, hint }) => {
  const { text, tone } = deltaView(deltaPct);
  const colors = tone === 'up' ? DELTA_TONES[kind] ?? DELTA_TONES.emerald : tone === 'down' ? DELTA_TONES.down : DELTA_TONES.neutral;
  return (
    <Box
      component="span"
      title={hint}
      data-testid="delta-badge"
      data-tone={tone}
      sx={{ ...colors, display: 'inline-flex', alignItems: 'center', gap: 0.25, px: 1, py: 0.25, borderRadius: '4px', fontSize: 12, fontWeight: 600, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
    >
      {tone === 'down' && <ArrowDownwardIcon sx={{ fontSize: 12 }} />}
      {text}
    </Box>
  );
};

const StaticBadge = ({ badge }) => (
  <Box component="span" sx={{ bgcolor: badge.bg, color: badge.color, px: 1, py: 0.25, borderRadius: '4px', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
    <LocalText>{badge.text}</LocalText>
  </Box>
);

const BreakdownLink = ({ open, onToggle }) => (
  <Box
    component="button"
    type="button"
    aria-expanded={open}
    // The card itself opens the popup; this link must only toggle the breakdown.
    onClick={(e) => { e.stopPropagation(); onToggle(); }}
    onKeyDown={(e) => e.stopPropagation()}
    sx={{ background: 'none', border: 0, p: 0, cursor: 'pointer', font: 'inherit', fontSize: 11, fontWeight: 600, color: T.sky[700], flexShrink: 0, '&:hover': { textDecoration: 'underline' } }}
  >
    <LocalText>{open ? 'Hide Breakdown' : 'View Breakdown'}</LocalText>
  </Box>
);

/**
 * One headline card (Total, DAU, WAU, MAU). Every card opens its user list.
 *
 * Memoised: every prop is a primitive or a module-level constant, so a page re-render
 * that does not change the numbers (opening the popup, a toast) does not re-render the overview cards.
 *
 * Footer right-hand side, in priority order: `onToggleBreakdown` (Total) -> `deltaKind` (DAU, WAU)
 * -> `badge` (MAU).
 */
const MetricCard = React.memo(function MetricCard({
  cardKey, label, valueText, valueColor, Icon, chip, hoverBorder, hoverLink = T.navy, caption = '',
  status, hasData, onOpen,
  deltaPct, deltaKind, deltaHint, badge,
  breakdownOpen, onToggleBreakdown,
}) {
  const open = () => onOpen(cardKey);
  const openLabel = useLocalText('View users');

  return (
    <Card
      elevation={0}
      data-testid={`metric-card-${cardKey}`}
      role="button"
      tabIndex={0}
      aria-label={`${label}: ${valueText}. ${openLabel}`}
      onClick={open}
      onKeyDown={activateOnKey(open)}
      sx={cardSx(hoverBorder)}
    >
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography
              className="ua-label"
              component="span"
              sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: T.slate[500], transition: 'color 0.2s' }}
            >
              <LocalText>{label}</LocalText>
            </Typography>
          </Box>
          <Box
            sx={{
              width: 28, height: 28, borderRadius: '8px', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: chip.bg, color: chip.color, transition: 'all 0.2s',
              '.MuiCard-root:hover &': { bgcolor: chip.hoverBg ?? chip.bg, color: chip.hoverColor ?? chip.color },
            }}
          >
            <Icon sx={{ fontSize: 16 }} />
          </Box>
        </Box>

        <Box sx={{ mt: 1.25 }}>
          <ValueSlot status={status} hasData={hasData} minHeight={48} testId={`metric-value-${cardKey}`}>
            <Typography
              component="span"
              sx={{
                fontSize: { xs: 30, sm: 36 }, fontWeight: 800, color: valueColor,
                letterSpacing: '-0.025em', lineHeight: 1.15, fontVariantNumeric: 'tabular-nums',
              }}
            >
              {valueText}
            </Typography>
          </ValueSlot>
          <Typography sx={{ fontSize: 11, color: T.slate[500], minHeight: 16, mt: 0.5 }}>
            {caption}
          </Typography>
        </Box>
      </Box>

      <Box sx={FOOTER_SX}>
        <Box component="span" aria-hidden="true" sx={viewUsersSx(hoverLink)}>
          <LocalText>View Users</LocalText>
          <ArrowForwardIcon sx={{ fontSize: 12 }} />
        </Box>

        {onToggleBreakdown ? (
          <BreakdownLink open={Boolean(breakdownOpen)} onToggle={onToggleBreakdown} />
        ) : deltaKind ? (
          <DeltaBadge deltaPct={deltaPct} kind={deltaKind} hint={deltaHint} />
        ) : badge ? (
          <StaticBadge badge={badge} />
        ) : null}
      </Box>
    </Card>
  );
});

export default MetricCard;

/**
 * Larger card used in the optional "Immersive / CMS" breakdown drawer. Same value slot, same
 * click-anywhere behaviour; no delta footer.
 */
export const BreakdownCard = React.memo(function BreakdownCard({
  cardKey, label, tag, accent, tagBg, tagBorder, subtitle, hint, valueText, Icon, iconBg, status, hasData, onOpen,
}) {
  const open = () => onOpen(cardKey);
  const openLabel = useLocalText('View users');

  return (
    <Card
      elevation={0}
      data-testid={`metric-card-${cardKey}`}
      role="button"
      tabIndex={0}
      aria-label={`${label}: ${valueText}. ${openLabel}`}
      onClick={open}
      onKeyDown={activateOnKey(open)}
      sx={{ ...cardSx(accent), p: 2 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              className="ua-label"
              component="span"
              sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: T.slate[500], transition: 'color 0.2s' }}
            >
              <LocalText>{label}</LocalText>
            </Typography>
            <Box component="span" sx={{ fontSize: 10, fontWeight: 600, px: 1, py: 0.25, borderRadius: '4px', bgcolor: tagBg, color: accent, border: `1px solid ${tagBorder}` }}>
              <LocalText>{tag}</LocalText>
            </Box>
          </Box>
          <Box sx={{ mt: 0.5 }}>
            <ValueSlot status={status} hasData={hasData} minHeight={40} skeletonWidth={72} testId={`metric-value-${cardKey}`}>
              <Typography component="span" sx={{ fontSize: { xs: 24, sm: 30 }, fontWeight: 800, color: accent, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                {valueText}
              </Typography>
            </ValueSlot>
          </Box>
          <Typography sx={{ fontSize: 11, color: T.slate[500], mt: 0.5 }}><LocalText>{subtitle}</LocalText></Typography>
        </Box>
        <Box
          sx={{
            width: { xs: 40, sm: 44 }, height: { xs: 40, sm: 44 }, borderRadius: '12px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: iconBg, color: accent, transition: 'all 0.2s',
            '.MuiCard-root:hover &': { bgcolor: accent, color: '#ffffff' },
          }}
        >
          <Icon sx={{ fontSize: { xs: 22, sm: 26 } }} />
        </Box>
      </Box>

      <Box sx={{ ...FOOTER_SX, mt: 1.5, pt: 1.25, minHeight: 0 }}>
        <Typography sx={{ fontSize: 11, color: T.slate[500] }}><LocalText>{hint}</LocalText></Typography>
        <Box component="span" aria-hidden="true" sx={{ ...viewUsersSx(accent), color: accent }}>
          <LocalText>View Users</LocalText>
          <ArrowForwardIcon sx={{ fontSize: 12 }} />
        </Box>
      </Box>
    </Card>
  );
});
